import React, { useMemo, useState } from 'react';
import type { FlowProjection } from '../../lib/mackFlowRead';
import { flowScenario, flowScenarioModel, renderFlowCapacity, renderFlowClassifier,
  renderFlowControls, renderFlowRotation, renderFlowSeasonality, FlowScenario } from '../../lib/mackFlowCore';
import { PhasesEvidenceSurface, panelNote, panelTitle } from './primitives';
import './FlowDashboard.css';

const empty: FlowScenario = { vix: null, session: null, targetMove: null };

// The accepted renderer/model is shared verbatim. The OS keeps its own existing
// authenticated read hook and evidence drawer, so mounting this view adds no
// reader, notification subscriber, transport or economic implementation.
export function FlowDashboard({ projection, onSelect }: {
  projection: FlowProjection | null; onSelect: (field: string) => void;
}) {
  const [scenario, setScenario] = useState<FlowScenario>(empty);
  const model = useMemo(() => flowScenarioModel({ scenario, markets: projection }), [scenario, projection]);
  const change = (key: keyof FlowScenario, input: string) => {
    const value = key === 'session' ? input : input === '' ? null : Number(input);
    setScenario(previous => flowScenario({ scenario: { ...previous, [key]: value } }));
  };
  // The reference's XAU/USD label maps to the governed Gold row. The exact
  // source instrument remains visible in the identity list and evidence drawer.
  const display = { ...model, rows: model.rows.map(row => row.id === 'xau_usd' ? { ...row, label: 'Gold' } : row) };
  return <div className="flow-dashboard" data-flow-formula-state={model.formulaState}>
    <PhasesEvidenceSurface>
      <div className="flow-panel-heading"><h2 style={panelTitle}>Capacity — C, the eliminator</h2><span>Formula execution held</span></div>
      <p style={panelNote}>What this market requires to move, and which participants could supply it. Retained sources inform the model; capacity remains governed.</p>
      <div className="flow-formula-context">
        <code>AdjustedTurnover = NormalTurnover × L · RequiredCapital = AdjustedTurnover × (TargetMove / Sigma)²</code>
        <code>DeployableCapital = Capital × AssetExposure × DeploymentFraction · C = min(1, DeployableCapital / RequiredCapital)</code>
        <span>Approved formula text only · Excel/Python owns execution · legacy R never replaces C</span>
      </div>
      {renderFlowControls(React, display, change, () => setScenario(empty))}
      {renderFlowCapacity(React, display, id => onSelect(`mack.market.${id}.last_price`))}
      <div className="flow-explanations">
        <p><strong>Impact.</strong> A single participant must meet the canonical capacity threshold. The reference’s impact character is not a live classification; capacity inputs remain held.</p>
        <p><strong>Coalition.</strong> Several participants require governed coalition evidence. Adding participant weights or copying the pictured R value does not establish a coalition.</p>
        <p><strong>Repricing.</strong> Governed repricing evidence is required. Failure to establish impact or coalition does not prove repricing. Character is separate from participant capacity.</p>
      </div>
      <p style={panelNote}>Selecting a session affects presence P only. Turnover and volatility keep their own bindings; realised volatility must not receive a second VIX adjustment.</p>
      <div className="flow-provider-identities" aria-label="Exact retained market providers">
        {display.rows.map(row => {
          const source = projection?.markets.find(market => market.market_id === row.id)?.price;
          return <div key={row.id}><strong>{row.label}</strong><span>{source ? `${source.publisher || source.producer} · ${typeof source.series_id === 'string' ? source.series_id : JSON.stringify(source.series_id)}` : 'Retained provider identity not read'}</span></div>;
        })}
      </div>
    </PhasesEvidenceSurface>
    <div className="flow-lower-grid">
      <PhasesEvidenceSurface><h2 style={panelTitle}>Contract classifier</h2>
        <p style={panelNote}>Bands are held in notional and converted to counts only with the exact contract and price. Spot XAU/USD is not a GC futures contract.</p>
        {renderFlowClassifier(React, display)}
      </PhasesEvidenceSurface>
      <PhasesEvidenceSurface><h2 style={panelTitle}>Liquidity seasonality — modifies V, not attribution</h2>
        {renderFlowSeasonality(React, display)}
        <p style={panelNote}>Five approved research scopes. Effects, compatible history, calendar bindings and overlap rules remain pending; no numerical liquidity adjustment is supplied.</p>
      </PhasesEvidenceSurface>
    </div>
    <PhasesEvidenceSurface><h2 style={panelTitle}>Asset-class rotation by stage</h2>
      {renderFlowRotation(React)}
      <p style={panelNote}>An installed static prior, not a measured current ranking or dominant stage. Participant seasonality and WindowEffect remain separate governed dependencies.</p>
    </PhasesEvidenceSurface>
  </div>;
}
